/**
 * 63.5 step 1: the ground's material tables (ARCHITECTURE §5.13 "63.5, the design"). The renderer's surface layers
 * (PLANET_LAYERS) and strata (PLANET_STRATA) already name what each band is made of; these tables say which material
 * each name is, and what a blow or a step does to each material. Every effect row is today's exactly (× 1, no rim; walk
 * × 1), so nothing moves until a later step turns a dial behind its switch. Part of src/constants.ts (re-exported).
 */
import type { PlanetArchetype, HazardType } from '../types/planet';
import type { GroundMaterial, GroundEffect, GroundWalk } from '../types/ground';
import type { BlowCause } from '../types/body';
import { DAMAGE_VS } from './damage';

/** A surface layer's material, by the layer's name in PLANET_LAYERS; a name not here reads as soil (the default ground). */
export const LAYER_MATERIAL: Record<PlanetArchetype | 'earth', Record<string, GroundMaterial>> = {
  thalia:  { basin: 'soil', grass: 'soil', meadow: 'soil', scree: 'rock', snow: 'snow' },
  bruma:   { darkIce: 'ice', frost: 'snow', ice: 'ice', snow: 'snow' },
  tephra:  { obsidian: 'rock', ash: 'ash', baked: 'rock', dust: 'ash', ember: 'rock' },
  murex:   { deep: 'soil', mauve: 'soil', lilac: 'soil', cap: 'soil' },
  pallor:  { shadow: 'soil', steppe: 'soil', bleach: 'salt', chalk: 'rock' },
  pelago:  { shallows: 'mud', flats: 'salt', mineral: 'salt', salt: 'salt' },
  ochrus:  { shade: 'sand', ochre: 'sand', sand: 'sand', pale: 'rock' },
  viridis: { floor: 'soil', canopy: 'soil', upper: 'soil', crown: 'soil' },
  sonora:  { hollow: 'rock', stone: 'rock', pale: 'rock', ring: 'rock' },
  earth:   { ocean: 'mud', shore: 'sand', grass: 'soil', highland: 'rock', snow: 'snow' },
};

/** A stratum's material, by the stratum's name in PLANET_STRATA (what a facet dug below the generated ground is made of); a name not here reads as rock. */
export const STRATUM_MATERIAL: Record<string, GroundMaterial> = {
  topsoil: 'soil', clay: 'soil', bedrock: 'rock', deep: 'rock', mantle: 'rock', soil: 'soil',
  packedIce: 'ice', permafrost: 'ice', frozenRock: 'rock',
  ash: 'ash', tuff: 'rock', basalt: 'rock', magma: 'rock',
  mycelium: 'soil', loam: 'soil', shale: 'rock',
  chalk: 'rock', marl: 'soil', limestone: 'rock',
  silt: 'mud', salt: 'salt', slate: 'rock',
  sand: 'sand', hardpan: 'rock', sandstone: 'rock', iron: 'rock',
  humus: 'soil', roots: 'soil',
  dust: 'ash', lattice: 'rock', hollowRock: 'rock', resonantCore: 'rock',
};

export const GROUND_MATERIALS: readonly GroundMaterial[] = ['soil', 'rock', 'sand', 'snow', 'ice', 'mud', 'ash', 'salt', 'scree'];   // (74.13 step 4: scree last, so every index before it holds)

export const SAME: GroundEffect = { radius: 1, depth: 1, rim: 0 };
const everyMaterial = <T,>(v: T): Record<GroundMaterial, T> => Object.fromEntries(GROUND_MATERIALS.map(m => [m, v])) as Record<GroundMaterial, T>;

/**
 * 63.5 step 3 (ARCHITECTURE §5.13 "63.5, the design"): what a shell's blast digs in each material — the crater's radius
 * and depth × these, and a rim raised round it (a share of the crater's centre depth). Read only behind the match's
 * the action clock's ground (63.5 step 6, decision 155: no switch; the turn game's plain ground: every crater × 1). The design's: soil the full bowl; rock a chipped, shallower
 * one with a higher rim; sand a wide slump with no rim; snow and ice as soil (their slippery floor is walking's, step 4);
 * ash a deep soft bowl. Mud and salt are lane F's first guesses (a soft slump; a crust that breaks with a low lip), for
 * the owner to tune, as 130 tunes the props'. All within 134's cap at person scale.
 */
export const GROUND_BLAST: Record<GroundMaterial, GroundEffect> = {
  soil: SAME,
  rock: { radius: 1, depth: 0.5, rim: 0.3 },
  sand: { radius: 1.2, depth: 0.7, rim: 0 },
  snow: SAME,
  ice: SAME,
  mud: { radius: 1.1, depth: 0.8, rim: 0 },
  ash: { radius: 1, depth: 1.3, rim: 0 },
  salt: { radius: 1, depth: 0.8, rim: 0.15 },
  scree: { radius: 1.1, depth: 0.6, rim: 0.1 },   // (74.13 step 4: loose rock — rock's shallow bowl, slumped a little wider, a low rim)
};

/**
 * DAMAGE_VS's earth column, split by the ground's material: what a blow does to a crater dug in it. Every row × 1, no
 * rim, but the shell's blast (GROUND_BLAST, behind the switch) — so the table moves nothing until the switch is on.
 */
export const GROUND_VS: Record<BlowCause, Record<GroundMaterial, GroundEffect>> = Object.fromEntries(
  (Object.keys(DAMAGE_VS) as BlowCause[]).map(cause => [cause, cause === 'blast' ? GROUND_BLAST : everyMaterial(SAME)]),
) as Record<BlowCause, Record<GroundMaterial, GroundEffect>>;   // every blow DAMAGE_VS knows, so the two tables never drift apart

/**
 * 63.5's reader 2 (ARCHITECTURE §5.13 "63.5, the design"): what a blow does to the things that are ground — `DAMAGE_VS`'s
 * `earth` column split into the ground's materials, each row today's earth share exactly (the split is the place a row is
 * tuned later; nothing moves now) — and which ground each terrain feature is: a vent's mouth rock, a geyser's ice, a
 * crystal stone (rock).
 */
export const GROUND_DAMAGE: Record<BlowCause, Record<GroundMaterial, number>> = Object.fromEntries(
  (Object.keys(DAMAGE_VS) as BlowCause[]).map(cause => [cause, Object.fromEntries(GROUND_MATERIALS.map(m => [m, DAMAGE_VS[cause].earth]))]),
) as Record<BlowCause, Record<GroundMaterial, number>>;
export const HAZARD_GROUND: Record<HazardType, GroundMaterial> = { volcanic: 'rock', geyser: 'ice', crystal: 'rock' };

/**
 * 63.5 step 4 (62.19 (e), 134): what walking each material costs — read on the action clock's ground (63.5 step 6: no switch)
 * (off: × 1 everywhere, as before). The design's dials: mud 0.7, sand 0.85, snow 0.8 (here 0.9 thin to 0.7 deep, by how
 * high in its band the snow lies), ice 1.0 with a slide and a slow turn, the scramble harder on rock (× 0.7) and easier on
 * soil. Lane C's first guesses, for the owner to tune: mud × 0.85 more in the rain; sand loses half its pace per unit of
 * grade uphill, ash a third; ice closes on the way asked at 3/s (about a third of a second to turn or stop) and drifts down
 * a slope; ash 0.9, salt a firm crust (× 1); the scramble on sand, snow, mud and ash × 0.85, on ice × 0.6.
 */
export const GROUND_WALK: Record<GroundMaterial, GroundWalk> = {
  soil: { speed: 1, scramble: 1.1 },
  rock: { speed: 1, scramble: 0.7 },
  sand: { speed: 0.85, scramble: 0.85, uphill: 0.5 },
  snow: { speed: 0.9, deep: 0.7, scramble: 0.85 },
  ice: { speed: 1, scramble: 0.6, grip: 3, slide: 0.6 },
  mud: { speed: 0.7, wet: 0.85, scramble: 0.85 },
  ash: { speed: 0.9, scramble: 0.85, uphill: 0.33 },
  salt: { speed: 1, scramble: 1 },
  scree: { speed: 0.85, scramble: 0.85, uphill: 0.5 },   // (74.13 step 4, 374 (5): loose rock slows the walk as sand does)
};

/**
 * 71.2 (DECISIONS 324, ARCHITECTURE §5.36 (3)): how far a thing on the ground sinks into it, by the ground's dry material,
 * in people — a rock, a tree, a fallen limb stands on the fine ground at its footprint's lowest point less this (core/propSeat.ts):
 * deep in snow, a little in sand, ash and mud, a touch in soil and salt, none on rock or ice. The sim and the drawing alike.
 */
export const PROP_SINK_PEOPLE: Record<GroundMaterial, number> = { soil: 0.04, rock: 0, sand: 0.1, snow: 0.3, ice: 0, mud: 0.08, ash: 0.1, salt: 0.02, scree: 0.04 };

/**
 * 71.2b (DECISIONS 324 (3), ARCHITECTURE §5.36 (2)): the fine relief by the ground's dry material — the relief is the
 * amplitude × (broad · the broad octave + fine · the fine octave + ripple · ripples on the broad octave's contours), each
 * octave a smoothed lattice noise in 0 … 1. Soil, ash and mud are today's hummocks (0.8 and 0.2); snow soft and broad (its
 * broad share from `snowThin` where its band begins to `broad` toward the peaks, 63.5's layer depth); sand low ripples;
 * rock at soil's hummocks in this arm (71.2b's second arm, offered to the owner beside rock crisp and low); ice and salt flat. Every share ≥ 0 and each row's sum ≤ 1, so the relief
 * stays bumps-only above the mesh and never above the amplitude (the sight march's and the shells' bound).
 */
export const RELIEF_BY_MATERIAL: Record<GroundMaterial, { broad: number; fine: number; ripple: number }> = {
  soil: { broad: 0.8, fine: 0.2, ripple: 0 },
  ash: { broad: 0.8, fine: 0.2, ripple: 0 },
  mud: { broad: 0.8, fine: 0.2, ripple: 0 },
  snow: { broad: 1, fine: 0, ripple: 0 },
  sand: { broad: 0.25, fine: 0, ripple: 0.15 },
  rock: { broad: 0.8, fine: 0.2, ripple: 0 },   // (the second arm: rock at soil's hummocks — the crisp, low rock (0.12, 0.18) put deaths a minute up 16 % in the Story, cover gone off the rock patches)
  ice: { broad: 0.05, fine: 0, ripple: 0 },
  salt: { broad: 0.05, fine: 0, ripple: 0 },
  scree: { broad: 0.6, fine: 0.4, ripple: 0 },   // (74.13 step 4: rubble — rock's hummocks, more of the fine octave)
};
/** 71.2b: snow's broad share where its band begins (thin snow over the ground's shapes); RELIEF_BY_MATERIAL.snow.broad at its deepest. */
export const RELIEF_SNOW_THIN = 0.7;
/** 71.2b: sand's ripples — crests per unit of the broad octave's value (about 2.4 people apart at human scale, where the broad octave is 6). */
export const RELIEF_RIPPLES = 6;

/**
 * 63.5's last reader, cover by material (the coordinator, Oct 12): what the ground does to a round. `through` — the most
 * ground, in people along its path, a round passes (0: it stops in it); `loss` — its speed share falls by e^(−loss ·
 * depth) through it, and under GROUND_BULLET_FLOOR it stops there. Rock, ice and salt stop it; soil stops it (and wears
 * under it — a later step); sand and ash need thickness: a berm thinner than a person lets it through, slowed; snow and
 * mud let more through. The one table the readers take: the bullets (core/bullets.ts), the cover map's ground posts,
 * lane E's dig look and lane C's entrench. Unplayed proposals (decision 121).
 */
export const GROUND_BULLETS: Record<GroundMaterial, { through: number; loss: number }> = {
  soil: { through: 0, loss: 0 }, rock: { through: 0, loss: 0 }, ice: { through: 0, loss: 0 }, salt: { through: 0, loss: 0 },
  sand: { through: 1, loss: 1.2 }, ash: { through: 1, loss: 1 }, mud: { through: 1.5, loss: 0.9 }, snow: { through: 2, loss: 0.7 },
  scree: { through: 0, loss: 0 },   // (74.13 step 4, 374 (5): it stops rounds as rock does)
};
/** A round slowed under this share of its muzzle speed by the ground it crossed stops in it. */
export const GROUND_BULLET_FLOOR = 0.25;
/**
 * 63.5 cover by material, the soil crest's wear (the coordinator's (b), Oct 12): every round into soil bumps its
 * person-sized cell's count (state.groundWear); past `hitsToPass` hits that soil passes rounds as sand does — a lip under
 * sustained fire stops being cover. `decayS`: the seconds a hit's wear lasts — 0, none decays (a dial left at off). The
 * dent itself (the ground lowered) is a later row; lane E's dig look can show the wear.
 */
export const GROUND_WEAR = { hitsToPass: 12, decayS: 0 };
